const trimTrailingSlash = (value) => value.replace(/\/+$/, '');

/** Never allow Organisation OAuth to complete on the consumer app domain. */
const CONSUMER_APP_ORIGINS = new Set([
  'https://app.surpassin.com',
  'http://app.surpassin.com',
]);

export const getPortalOrigin = () => {
  // Always prefer the live browser origin so OAuth callbacks stay on the
  // current portal (organisation.*), even if a shared env var points at app.*.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return trimTrailingSlash(window.location.origin);
  }
  const configured = import.meta.env.VITE_APP_ORIGIN;
  if (typeof configured === 'string' && configured.trim()) {
    return trimTrailingSlash(configured.trim());
  }
  return '';
};

export const getPortalUrl = (path = '/') => {
  const origin = getPortalOrigin();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalizedPath}`;
};

export const getSocialAuthCallbackUrl = () => getPortalUrl('/social-auth/callback');

/**
 * Absolute redirect config for authenticateWithRedirect / handleRedirectCallback.
 * Force URLs override Clerk Dashboard "home" (often app.surpassin.com).
 */
export const buildClerkRedirectConfig = (strategy) => {
  const callbackUrl = getSocialAuthCallbackUrl();
  const loginUrl = getPortalUrl('/auth/login');
  const config = {
    redirectUrl: callbackUrl,
    redirectUrlComplete: callbackUrl,
    // Clerk v5+: force always wins over Dashboard default / stale redirect_url.
    forceRedirectUrl: callbackUrl,
    signInForceRedirectUrl: callbackUrl,
    signUpForceRedirectUrl: callbackUrl,
    signInFallbackRedirectUrl: loginUrl,
    signUpFallbackRedirectUrl: callbackUrl,
    continueSignUpUrl: callbackUrl,
  };
  if (strategy) {
    config.strategy = strategy;
  }
  return config;
};

export const getClerkAllowedRedirectOrigins = () => {
  const origins = new Set();
  const portalOrigin = getPortalOrigin();
  if (portalOrigin) {
    origins.add(portalOrigin);
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    origins.add(window.location.origin);
  }

  const extra = import.meta.env.VITE_CLERK_ALLOWED_REDIRECT_ORIGINS;
  if (typeof extra === 'string' && extra.trim()) {
    extra
      .split(',')
      .map((origin) => origin.trim())
      .filter(Boolean)
      .forEach((origin) => origins.add(origin));
  }

  if (import.meta.env.DEV) {
    origins.add('http://localhost:5173');
    origins.add('http://localhost:5174');
    origins.add('http://localhost:3000');
  }

  // Organisation portal must never treat the consumer app as a safe OAuth landing.
  CONSUMER_APP_ORIGINS.forEach((blocked) => origins.delete(blocked));

  return [...origins];
};

const normalizeHost = (value) =>
  value.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');

const getSatelliteDomain = () => {
  const configured = import.meta.env.VITE_CLERK_SATELLITE_DOMAIN;
  if (typeof configured === 'string' && configured.trim()) {
    return normalizeHost(configured);
  }

  const origin = getPortalOrigin();
  if (!origin) {
    return '';
  }

  try {
    return new URL(origin).host;
  } catch {
    return '';
  }
};

const resolvePrimaryAuthUrl = (configuredValue, fallbackPath) => {
  if (typeof configuredValue === 'string' && configuredValue.trim()) {
    const value = configuredValue.trim();
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return trimTrailingSlash(value);
    }
  }

  const primaryDomain = import.meta.env.VITE_CLERK_PRIMARY_DOMAIN;
  if (typeof primaryDomain !== 'string' || !primaryDomain.trim()) {
    return '';
  }

  const path =
    (typeof configuredValue === 'string' && configuredValue.trim()) ||
    fallbackPath;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `https://${normalizeHost(primaryDomain)}${normalizedPath}`;
};

/**
 * ClerkProvider props for Organisation portal.
 * Force redirects keep OAuth completion on this origin even when the shared
 * Clerk instance Dashboard home URL is app.surpassin.com.
 */
export const getClerkProviderProps = () => {
  const callbackUrl = getSocialAuthCallbackUrl();
  const loginUrl = getPortalUrl('/auth/login');

  const props = {
    allowedRedirectOrigins: getClerkAllowedRedirectOrigins(),
    signInForceRedirectUrl: callbackUrl,
    signUpForceRedirectUrl: callbackUrl,
    signInFallbackRedirectUrl: loginUrl,
    signUpFallbackRedirectUrl: callbackUrl,
  };

  // Subdomains of the same root share Clerk sessions by default.
  // Satellite mode is only for different top-level domains.
  if (import.meta.env.VITE_CLERK_IS_SATELLITE !== 'true') {
    return props;
  }

  const satelliteDomain = getSatelliteDomain();
  if (!satelliteDomain) {
    console.warn(
      '[Clerk] VITE_CLERK_IS_SATELLITE is enabled but no satellite domain was resolved. ' +
        'Set VITE_APP_ORIGIN or VITE_CLERK_SATELLITE_DOMAIN, or disable satellite mode.',
    );
    return props;
  }

  props.isSatellite = true;
  props.domain = satelliteDomain;

  const signInUrl = resolvePrimaryAuthUrl(
    import.meta.env.VITE_CLERK_SIGN_IN_URL,
    '/auth/login',
  );
  const signUpUrl = resolvePrimaryAuthUrl(
    import.meta.env.VITE_CLERK_SIGN_UP_URL,
    '/auth/register',
  );
  if (signInUrl) {
    props.signInUrl = signInUrl;
  }
  if (signUpUrl) {
    props.signUpUrl = signUpUrl;
  }

  return props;
};

export const redirectToSocialAuthCallback = (resume = false) => {
  window.location.assign(
    resume
      ? getPortalUrl('/social-auth/callback?resume=1')
      : getSocialAuthCallbackUrl(),
  );
};
